import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, OnInit, output, signal, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { take } from 'rxjs';

import { AudioDeviceListComponent, AudioDropdownItem, MediaResourceService } from '@libs/shared-services';
import { animate, style, transition, trigger } from '@angular/animations';
import { localStorageKey } from '@libs/utils';
import { PerioPositionIndicatorComponent } from '../perio-position-indicator/perio-position-indicator.component';
import { PerioTooltipToothSurface } from './perio-exam-toolbar.interface';
import { PerioExamTutorialComponent } from './components';

const VOLUME_MULTIPLIER = 4;
@Component({
  selector: ' lib-perio-exam-toolbar',
  standalone: true,
  imports: [
    CommonModule,
    SvgIconComponent,
    TranslateModule,
    PerioPositionIndicatorComponent,
    LottieComponent,
    OverlayModule,
    ReactiveFormsModule,
    AudioDeviceListComponent,
    PerioExamTutorialComponent,
  ],
  templateUrl: './perio-exam-toolbar.component.html',
  styleUrl: './perio-exam-toolbar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('expand', [
      transition(':enter', [style({ height: 0, overflow: 'hidden' }), animate('300ms ease-in-out', style({ height: '*' }))]),
      transition(':leave', [style({ height: '*', overflow: 'hidden' }), animate('300ms ease-in-out', style({ height: 0 }))]),
    ]),
  ],
})
export class PerioExamToolbarComponent implements OnInit {
  public isReviewActive = input.required<boolean>();
  public isVoiceActive = input.required<boolean>();
  public isVoiceLoading = input.required<boolean>();
  public volumeLevel = input.required<number>();
  public profileId = input.required<string>();
  public readonly volumeLevelPercent = computed(() => `${this.volumeLevel() * VOLUME_MULTIPLIER}%`);

  public toothList = input.required<PerioTooltipToothSurface[]>();

  public reviewClick = output<void>();
  public voiceToggle = output<void>();
  public selectDevice = output<string>();
  public toothNavigate = output<PerioTooltipToothSurface>();

  private readonly mediaResourceService = inject(MediaResourceService);
  private readonly translateService = inject(TranslateService);

  public readonly audioInputDropdownIsOpen = signal(false);
  public readonly selectedDeviceControl = new FormControl<string | null>(null);
  public readonly audioInputOverlay = viewChild.required('audioInputOverlay', { read: ElementRef });
  public readonly microphoneDevices = signal<MediaDeviceInfo[]>([]);
  public readonly tutorialOpened = signal<boolean>(false);
  public readonly builtInDevices = computed<AudioDropdownItem<string>[]>(() => {
    const devices = this.microphoneDevices();
    return devices.map((device) => ({
      value: device.deviceId,
      label: device.label,
    }));
  });

  public readonly positions: ConnectedPosition[] = [
    {
      originX: 'center',
      originY: 'bottom',
      overlayX: 'center',
      overlayY: 'top',
      offsetY: 8,
    },
  ];

  public get dropdownIcon(): 'px-16-arrow-up' | 'px-16-arrow-down' {
    return this.audioInputDropdownIsOpen() ? 'px-16-arrow-up' : 'px-16-arrow-down';
  }

  public lottieOptions: AnimationOptions = {
    path: '/assets/animations/loading-spinner.json',
  };

  public ngOnInit(): void {
    this.tutorialOpened.set(!JSON.parse(localStorage.getItem(`${localStorageKey.hidePerioExamTutorial}_${this.profileId()}`) || 'false'));
  }

  public onMicSettingsToggle(): void {
    this.audioInputDropdownIsOpen.update((v) => !v);
    if (this.audioInputDropdownIsOpen()) {
      // Fetch fresh device list when opening the dropdown
      this.mediaResourceService.microphoneDevices$.pipe(take(1)).subscribe((devices) => {
        this.microphoneDevices.set(devices);
        // Set the form control to the current active device or default device
        const currentDeviceId = this.selectedDeviceControl.value;
        const defaultDeviceId = this.mediaResourceService.defaultDeviceId$.value;
        const deviceToSelect = currentDeviceId || defaultDeviceId;
        if (deviceToSelect) {
          this.selectedDeviceControl.setValue(deviceToSelect, { emitEvent: false });
        }
      });
    }
  }

  public onBackdropClick(): void {
    this.onCancel();
  }

  public onCancel(): void {
    this.audioInputDropdownIsOpen.set(false);
  }

  public onDone(): void {
    const selectedDeviceId = this.selectedDeviceControl.value;
    if (selectedDeviceId) {
      this.selectDevice.emit(selectedDeviceId);
    }
    this.audioInputDropdownIsOpen.set(false);
  }

  protected onTutorialClick(): void {
    this.tutorialOpened.update((opened) => !opened);
  }

  protected onTutorialClose(): void {
    this.tutorialOpened.set(false);
  }

  protected onToothNavigate(tooth: PerioTooltipToothSurface): void {
    this.toothNavigate.emit(tooth);
  }
}
